import { TestRunAttemptStatus, type TestRunStatusHistory, type RunStatus } from '@prisma/client';
import { useMemo } from 'react';
import StatusIcon from './StatusIcon';
import { Tooltip } from '@mantine/core';
import { getEarliestStartTime } from '../tables/testRun/testRunColumns';
import { capFirst } from '~/common/string/capitalize';

const DEFAULT_ICON_SIZE = 20;

type TestRunAttemptIconData = {
  id: string;
  startTime: Date;
  status: TestRunAttemptStatus;
  isRerun: boolean;
  isManual: boolean;
};

type TestRunAttemptResult = {
  startTime: Date | undefined;
  status: RunStatus | 'FLAKY';
  id: string;
};

// TODO: In the future, group the attempts by the run that triggered them,
// and display the status of that run instead of doing it manually here.
export default function LayeredRunAttemptStatusIcons({
  statusHistory,
  attempts = [],
  size = DEFAULT_ICON_SIZE,
  runStatus,
}: {
  statusHistory?: TestRunStatusHistory[];
  attempts: TestRunAttemptIconData[];
  size?: number;
  runStatus?: RunStatus;
}) {
  const recentResults = useMemo(() => {
    if (!attempts || attempts.length === 0) {
      return [];
    }

    // if any are running, just show the running icon
    if (attempts.some((attempt) => attempt.status === 'RUNNING')) {
      // just one test, so the startTime won't be displayed
      return [{ startTime: undefined, status: TestRunAttemptStatus.RUNNING, id: attempts[0]!.id }];
    }

    // Separate the original attempts (containing retries) from the rerun/manual attempts
    const { original = [], rerunOrManual = [] } = Object.groupBy(attempts, (attempt) =>
      attempt.isRerun || attempt.isManual ? 'rerunOrManual' : 'original',
    );

    // in case of an overriden result, show only that
    if (runStatus && statusHistory && statusHistory.length > 0) {
      return [{ startTime: undefined, status: runStatus, id: attempts[0]!.id }];
    }

    // Get the last 3 runs from the rerun/manual groups (most recent first)
    // include the original run in the results if there are fewer than 3
    const results: TestRunAttemptResult[] = rerunOrManual
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
      .slice(0, 3)
      .map((attempt) => ({ startTime: attempt.startTime, status: attempt.status, id: attempt.id }));

    if (results.length < 3 && original.length > 0) {
      const firstAttemptStatus = original[0]?.status;
      const status = original.some((attempt) => attempt.status !== firstAttemptStatus) ? 'FLAKY' : firstAttemptStatus;
      const earliestStartTime = getEarliestStartTime(original) ?? original[0]!.startTime;
      results.push({ startTime: earliestStartTime, status, id: original[0]!.id });
    }

    return results;
  }, [attempts]);

  // When the test has the SKIPPED, QUEUED, or CANCELLED runStatus, it may not have any attempts.
  // In these cases, we should show the icon according to the runStatus.
  if (runStatus === 'SKIPPED' || runStatus === 'QUEUED' || runStatus === 'CANCELLED') {
    return (
      <StatusIcon
        status={runStatus}
        size={size}
        statusHistory={statusHistory}
      />
    );
  }

  if (!attempts || attempts.length === 0) {
    return;
  }

  const layerOffset = Math.max(2, size * 0.2); // Offset between layers

  return (
    <Tooltip
      label={
        <>
          {recentResults.map((attempt) => (
            <div
              key={attempt.id}
              className="text-xs"
            >
              {capFirst(attempt.status.toLowerCase())} (
              {attempt.startTime?.toLocaleString('en-US', {
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              })}
              )
            </div>
          ))}
        </>
      }
      disabled={recentResults.length === 1}
      multiline
    >
      <div
        className="relative"
        style={{
          width: size + (recentResults.length - 1) * layerOffset,
          height: size + (recentResults.length - 1) * layerOffset,
        }}
      >
        {recentResults.map((attempt, index) => {
          const zIndex = recentResults.length - index; // Most recent has highest z-index
          const offset = index * layerOffset;

          return (
            <div
              key={attempt.id}
              className="absolute rounded-full bg-[var(--mantine-color-body)]"
              style={{
                left: offset,
                top: offset,
                zIndex,
                width: size,
                height: size,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StatusIcon
                status={attempt.status}
                size={size}
                filled={attempt.status !== 'FLAKY'}
                showTooltip={recentResults.length === 1}
                statusHistory={statusHistory}
              />
            </div>
          );
        })}
      </div>
    </Tooltip>
  );
}
